import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { VStack, Text, HStack, Input, Heading } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import { DateTime, hours, utc, zone } from "timezonecomplete"
import { ActivityCreateProps } from "./screens"
import {
  useCreateFormData,
  useActivityWorkflowActions,
  useAvailabilityWarning,
} from "@hydrologiq/shared/contexts/ActivityWorkflowContextV2"
import { formatActivityKind } from "@hydrologiq/shared/utils/activities/activityStatus"
import SearchCombobox from "@hydrologiq/shared/components/inputs/SearchCombobox"
import { useSearchQuery as useSearchQueryBase, QueryType } from "@hydrologiq/shared/hooks/useSearchQuery"
import type { UseSearchQueryHook } from "@hydrologiq/shared/components/inputs/SearchCombobox"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import ActivityCreateScreenWrapper from "./ActivityCreateScreenWrapper"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import FormContainer from "@hydrologiq/shared/chakra/custom/FormContainer"
import ActivityCreateLoading from "./ActivityCreateLoading"

interface ActivityCreateHomeProps extends ActivityCreateProps {
  activityKinds?: gqlTypes.ActivityKind[]
  /** When set (e.g. creating from an order row), pre-selects order and fulfilment site. */
  order?: gqlTypes.Order
  asset?: gqlTypes.Asset
  defaultStartDate?: string
  defaultEndDate?: string
  defaultQuantity?: number
  defaultSite?: string
  availableAssets?: gqlTypes.Asset[]
  /** When true, skips the automatic order pre-population logic (use when providing pre-filled data) */
  skipOrderAutoPopulation?: boolean
  onValidationChange?: (isValid: boolean) => void
  onActivityKindChange?: (kind: gqlTypes.ActivityKind) => void
}

const getDefaultDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return ""
  try {
    const tz = zone(IANATimeZone())
    let dt: DateTime
    if (!isoDate.includes("Z") && !isoDate.includes("+") && !isoDate.includes("-", 10)) {
      dt = new DateTime(isoDate, utc()).toZone(tz)
    } else {
      dt = new DateTime(isoDate).toZone(tz)
    }
    return dt.format("yyyy-MM-dd'T'09:00")
  } catch (e) {
    console.error("Error parsing date:", e, isoDate)
    return ""
  }
}

const getDefaultEndDateTime = (startDateTime: string, endDateTime?: string) => {
  try {
    const tz = zone(IANATimeZone())
    const start = new DateTime(startDateTime, "YYYY-MM-DD'T'HH:mm", tz)
    const end = endDateTime ? new DateTime(endDateTime, "YYYY-MM-DD'T'HH:mm", tz) : start.add(hours(1))
    return end.format("yyyy-MM-dd'T'HH:mm")
  } catch (e) {
    console.error("Error calculating default end date:", e)
    return startDateTime
  }
}

/** Primary customer site on the order (matches orders table / order dialog conventions). */
const getOrderSiteId = (o: gqlTypes.Order | null | undefined): string | null => o?.sites?.[0]?.site?.id ?? null

const ActivityCreateHome = ({
  activityKinds = [gqlTypes.ActivityKind.Filling, gqlTypes.ActivityKind.Movement],
  order: orderFromContext,
  asset,
  defaultStartDate,
  defaultEndDate,
  defaultQuantity,
  defaultSite,
  availableAssets: availableAssetsFromProps,
  skipOrderAutoPopulation = false,
  onScreenChange, // eslint-disable-line @typescript-eslint/no-unused-vars
  onClose, // eslint-disable-line @typescript-eslint/no-unused-vars
  onValidationChange,
  onActivityKindChange,
}: ActivityCreateHomeProps) => {
  const createFormData = useCreateFormData()
  const { updateCreateFormField, initialiseCreateFormData, setAvailabilityWarning } = useActivityWorkflowActions()
  const availabilityWarning = useAvailabilityWarning()

  useEffect(() => {
    if (createFormData || !activityKinds) return
    initialiseCreateFormData(activityKinds)
  }, [activityKinds])

  // Pre-populate asset from prop when form is initialised
  useEffect(() => {
    if (!createFormData || !asset?.id) return

    updateCreateFormField("attributes", {
      ...createFormData.attributes,
      asset: asset.id,
    })

    // When creating an activity on a specific asset, limit available assets to just that asset
    // This takes precedence over order-based asset selection
    updateCreateFormField("availableAssets", [asset] as gqlTypes.Asset[])
    // Only when the form first appears or asset id changes — not on every createFormData update (that would overwrite site after order select).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!createFormData, asset?.id])

  // Pre-select order, site, and assets when opening fulfilment create from an order context
  // Skip this effect entirely if skipOrderAutoPopulation is true (when using pre-filled data)
  useEffect(() => {
    if (skipOrderAutoPopulation) return
    if (!createFormData || !orderFromContext?.id) return
    if (createFormData.kind !== gqlTypes.ActivityKind.Fulfilment) return
    if (createFormData.order !== undefined) return

    updateCreateFormField("order", orderFromContext)
    if (orderFromContext.assets?.length) {
      updateCreateFormField("availableAssets", orderFromContext.assets as gqlTypes.Asset[])
    }
    updateCreateFormField("attributes", {
      ...createFormData.attributes,
      site: getOrderSiteId(orderFromContext),
      quantity: "",
      asset: orderFromContext.assets?.[0]?.id ?? null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply once when form has no order yet
  }, [createFormData?.kind, createFormData?.order, orderFromContext?.id])

  // Pre-populate dates from defaultStartDate when form is initialised
  useEffect(() => {
    if (!createFormData || !defaultStartDate) return
    // Only set if dates aren't already set
    if (createFormData.start || createFormData.end) return

    const startDate = getDefaultDate(defaultStartDate)
    if (startDate) {
      updateCreateFormField("start", startDate)
      updateCreateFormField("end", getDefaultEndDateTime(startDate))
    }
  }, [createFormData, defaultStartDate])

  // Pre-populate end date from defaultEndDate when provided
  useEffect(() => {
    if (!createFormData || !defaultEndDate) return
    // Only set if end date isn't already set
    if (createFormData.end) return

    const endDate = getDefaultDate(defaultEndDate)
    if (endDate) {
      updateCreateFormField("end", endDate)
    }
  }, [createFormData, defaultEndDate])

  // Pre-populate quantity from defaultQuantity when provided
  useEffect(() => {
    if (!createFormData || defaultQuantity === undefined) return
    // Only set if quantity isn't already set
    if (createFormData.quantity_scheduled) return

    updateCreateFormField("quantity_scheduled", defaultQuantity.toString())
    updateCreateFormField("attributes", {
      ...createFormData.attributes,
      quantity: defaultQuantity.toString(),
    })
  }, [createFormData, defaultQuantity])

  // Pre-populate site from defaultSite when provided
  useEffect(() => {
    if (!createFormData || !defaultSite) return
    // Only set if site isn't already set
    if (createFormData.attributes.site) return

    updateCreateFormField("attributes", {
      ...createFormData.attributes,
      site: defaultSite,
    })
  }, [createFormData, defaultSite])

  // When skipOrderAutoPopulation is true, manually set order and pre-fill data
  // This runs AFTER the defaultQuantity effect, so we preserve the quantity that was set
  useEffect(() => {
    if (!skipOrderAutoPopulation) return
    if (!createFormData || !orderFromContext?.id) return
    if (createFormData.order !== undefined) return
    
    // Set order without clearing quantity
    updateCreateFormField("order", orderFromContext)
    
    // Set available assets if provided
    if (availableAssetsFromProps?.length) {
      updateCreateFormField("availableAssets", availableAssetsFromProps)
    }
    
    // Set site and asset, preserving quantity if it's been set
    // Use quantity_scheduled as the source of truth since it's set correctly
    const quantityToUse = createFormData.quantity_scheduled || createFormData.attributes.quantity
    updateCreateFormField("attributes", {
      ...createFormData.attributes,
      site: defaultSite || getOrderSiteId(orderFromContext),
      asset: availableAssetsFromProps?.[0]?.id ?? orderFromContext.assets?.[0]?.id ?? null,
      quantity: quantityToUse || createFormData.attributes.quantity,
    })
  }, [createFormData, orderFromContext?.id, skipOrderAutoPopulation, availableAssetsFromProps, defaultSite])

  // Pre-populate available assets from prop when provided (for non-skip cases)
  useEffect(() => {
    if (skipOrderAutoPopulation) return // Handled above
    if (!createFormData || !availableAssetsFromProps?.length) return
    // Only set if available assets aren't already set
    if (createFormData.availableAssets?.length) return

    updateCreateFormField("availableAssets", availableAssetsFromProps)
    // Pre-select the first available asset
    if (availableAssetsFromProps[0]?.id) {
      updateCreateFormField("attributes", {
        ...createFormData.attributes,
        asset: availableAssetsFromProps[0].id,
      })
    }
  }, [createFormData, availableAssetsFromProps, skipOrderAutoPopulation])

  // Notify parent when activity kind changes
  useEffect(() => {
    if (createFormData?.kind) {
      onActivityKindChange?.(createFormData.kind)
    }
  }, [createFormData?.kind, onActivityKindChange])

  const [dateValidationError, setDateValidationError] = useState<string | null>(null)
  const [lastChangedField, setLastChangedField] = useState<"start" | "end" | null>(null)
  const lastCheckedParamsRef = useRef<string | null>(null)

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
  })

  // Wrapper hook for useSearchQuery to match SearchCombobox's expected signature
  const useSearchQuery: UseSearchQueryHook<gqlTypes.Order> = (params) => {
    return useSearchQueryBase({
      queryType: params.queryType as QueryType,
      searchTerm: params.searchTerm,
      debounceMs: params.debounceMs,
      enabled: params.enabled,
      allowEmptySearch: params.allowEmptySearch,
      additionalVariables: params.additionalVariables,
    }) as any
  }

  // GraphQL query for checking activity availability
  const [checkAvailabilityQuery] = useLazyQueryWithEndpoint(
    gqlTypes.CheckActivityAvailabilityDocument,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data?.checkActivityAvailability) {
          const result = data.checkActivityAvailability
          if (!result.available && result.conflicts && result.conflicts.length > 0) {
            try {
              const conflict = result.conflicts[0]
              const tz = zone(IANATimeZone())
              const endWithTz = (date: string) => (date.includes("Z") ? date : date + "Z")
              const conflictDate = new DateTime(endWithTz(conflict.start), utc()).toZone(tz).format("MMM dd, YYYY")

              const activityKind = conflict.activity_kind || "activity"
              const activityName = conflict.activity_name ? ` (${conflict.activity_name})` : ""
              const warningMsg = `There is already a ${activityKind} activity${activityName} during the specified time period on ${conflictDate}.`

              setAvailabilityWarning(warningMsg)
            } catch (error) {
              console.error("Error parsing conflict date:", error)
            }
          } else {
            setAvailabilityWarning(null)
          }
        }
      },
      onError: (error) => {
        console.error("Error checking availability:", error)
      },
    },
    "primary"
  )

  // Check activity availability
  const checkAvailability = async (start: string, end: string, assetId: string) => {
    // Create a unique key for these parameters to avoid redundant checks
    const paramsKey = `${assetId}-${start}-${end}`
    if (lastCheckedParamsRef.current === paramsKey) {
      return // Already checked this combination
    }
    lastCheckedParamsRef.current = paramsKey

    try {
      const tz = zone(IANATimeZone())
      const startDt = new DateTime(start, "YYYY-MM-DD'T'HH:mm", tz)
      const endDt = new DateTime(end, "YYYY-MM-DD'T'HH:mm", tz)

      await checkAvailabilityQuery({
        variables: {
          assets: assetId,
          start: startDt.toZone(utc()).toIsoString(),
          end: endDt.toZone(utc()).toIsoString(),
          full_day_check: false,
        },
      })
    } catch (error) {
      console.error("Error in checkAvailability:", error)
    }
  }

  // Validate dates whenever they change
  useEffect(() => {
    if (createFormData?.start && createFormData?.end) {
      try {
        const tz = zone(IANATimeZone())
        const start = new DateTime(createFormData.start, "YYYY-MM-DD'T'HH:mm", tz)
        const end = new DateTime(createFormData.end, "YYYY-MM-DD'T'HH:mm", tz)

        if (end.valueOf() <= start.valueOf()) {
          if (lastChangedField === "end") {
            setDateValidationError("End date/time must be after start date/time")
          }
        } else {
          setDateValidationError(null)
        }
      } catch {
        setDateValidationError(null)
      }
    } else {
      setDateValidationError(null)
    }
  }, [createFormData?.start, createFormData?.end, lastChangedField])

  // Check availability when dates or asset changes
  useEffect(() => {
    const assetId = createFormData?.attributes.asset || asset?.id
    if (!createFormData?.start || !createFormData?.end || !assetId) {
      return
    }

    // Clear warning and reset tracking if there's a date validation error
    if (dateValidationError) {
      setAvailabilityWarning(null)
      lastCheckedParamsRef.current = null
      return
    }

    const timeoutId = setTimeout(() => {
      checkAvailability(createFormData.start!, createFormData.end!, assetId)
    }, 500)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createFormData?.start, createFormData?.end, createFormData?.attributes.asset, asset?.id, dateValidationError])

  const isValid = useCallback(() => {
    const hasRequiredDates = !!createFormData?.start && !!createFormData?.end
    const hasRequiredOrder = createFormData?.kind !== gqlTypes.ActivityKind.Fulfilment || !!createFormData?.order
    return !dateValidationError && !availabilityWarning && hasRequiredDates && hasRequiredOrder
  }, [
    dateValidationError,
    availabilityWarning,
    createFormData?.start,
    createFormData?.end,
    createFormData?.kind,
    createFormData?.order,
  ])

  // Notify parent of validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid())
    }
  }, [
    dateValidationError,
    availabilityWarning,
    createFormData?.start,
    createFormData?.end,
    createFormData?.kind,
    createFormData?.order,
    onValidationChange,
    isValid,
  ])

  // Memoize activity kinds to prevent recreation on every render
  const mappedActivityKinds = [
    {
      value: gqlTypes.ActivityKind.Filling,
      label: "Filling",
    },
    {
      value: gqlTypes.ActivityKind.Movement,
      label: "Movement",
    },
    {
      value: gqlTypes.ActivityKind.Maintenance,
      label: "Maintenance",
    },
    {
      value: gqlTypes.ActivityKind.Inspection,
      label: "Inspection",
    },
    {
      value: gqlTypes.ActivityKind.Fulfilment,
      label: "Fulfilment",
    },
    {
      value: gqlTypes.ActivityKind.Hold,
      label: "Hold",
    },
  ]
  const filteredActivityKinds = useMemo(
    () => mappedActivityKinds.filter((k) => activityKinds.includes(k.value)),
    [activityKinds]
  )

  if (!createFormData) return <ActivityCreateLoading />

  return (
    <ActivityCreateScreenWrapper>
      <FormContainer loading={false} canSubmit={true} useFormTag={true}>
        {(dateValidationError || availabilityWarning) && (
          <ErrorAlert
            title={dateValidationError ? "Validation Error" : "Scheduling Conflict"}
            message={dateValidationError || availabilityWarning || ""}
            onClose={() => {
              setDateValidationError(null)
              setAvailabilityWarning(null)
            }}
          />
        )}

        <VStack gap={4} align="100%">
          {/* Activity Details Section */}
          {createFormData.kind !== gqlTypes.ActivityKind.Fulfilment && (
            <VStack gap={4} align="stretch">
              <Heading size="md" as="h3">
                Create activity of type
              </Heading>
              <HStack gap={2} justify="space-between">
                <Field required>
                  <NativeSelectRoot>
                    <NativeSelectField
                      name="activityKind"
                      value={createFormData.kind}
                      onChange={(e) => {
                        const newKind = e.target.value as gqlTypes.ActivityKind
                        updateCreateFormField("kind", newKind)
                        onActivityKindChange?.(newKind)
                      }}
                      items={filteredActivityKinds.map((activityKind) => ({
                        label: activityKind.label,
                        value: activityKind.value,
                      }))}
                    />
                  </NativeSelectRoot>
                </Field>
              </HStack>
            </VStack>
          )}

          {/* Order selector for Fulfilment activities */}
          {createFormData.kind === gqlTypes.ActivityKind.Fulfilment && (
            <VStack gap={4} align="stretch">
              <Heading size="md" as="h3">
                Select an order
              </Heading>
              <Field label={`${copy[COPY_KEYS.HEADINGS.ORDER]}`} required>
                <SearchCombobox<gqlTypes.Order>
                  queryType="orders"
                  label="orders"
                  placeholder={`Select an ${copy[COPY_KEYS.HEADINGS.ORDER]}`}
                  onItemSelection={(selectedItem) => {
                    updateCreateFormField("order", selectedItem ?? undefined)
                    if (selectedItem?.assets) {
                      updateCreateFormField("availableAssets", selectedItem.assets as gqlTypes.Asset[])
                    }
                    updateCreateFormField("attributes", {
                      ...createFormData.attributes,
                      site: getOrderSiteId(selectedItem),
                      quantity: "",
                      asset: selectedItem?.assets?.[0]?.id || null,
                    })
                  }}
                  useSearchQuery={useSearchQuery}
                  loadInitialItems={true}
                  value={createFormData.order || null}
                  itemContent={(item) => {
                    const tz = zone(IANATimeZone())
                    let formattedStartDate = ""
                    let formattedEndDate = ""

                    if (item.start) {
                      try {
                        let dt: DateTime
                        if (!item.start.includes("Z") && !item.start.includes("+") && !item.start.includes("-", 10)) {
                          dt = new DateTime(item.start, utc()).toZone(tz)
                        } else {
                          dt = new DateTime(item.start).toZone(tz)
                        }
                        formattedStartDate = dt.format("MMM dd, YYYY")
                      } catch (e) {
                        console.error(`Error parsing ${copy[COPY_KEYS.HEADINGS.ORDER]} start date:`, e)
                        formattedStartDate = item.start
                      }
                    }

                    if (item.end) {
                      try {
                        let dt: DateTime
                        if (!item.end.includes("Z") && !item.end.includes("+") && !item.end.includes("-", 10)) {
                          dt = new DateTime(item.end, utc()).toZone(tz)
                        } else {
                          dt = new DateTime(item.end).toZone(tz)
                        }
                        formattedEndDate = dt.format("MMM dd, YYYY")
                      } catch (e) {
                        console.error(`Error parsing ${copy[COPY_KEYS.HEADINGS.ORDER]} end date:`, e)
                        formattedEndDate = item.end
                      }
                    }

                    return (
                      <VStack gap={0} align="start">
                        <Text textStyle="sm" fontWeight="medium">
                          {item.name}
                        </Text>
                        {(formattedStartDate || formattedEndDate) && (
                          <Text textStyle="xs" color="fg.muted">
                            {formattedStartDate && formattedEndDate
                              ? `${formattedStartDate} - ${formattedEndDate}`
                              : formattedStartDate
                              ? `Start: ${formattedStartDate}`
                              : `End: ${formattedEndDate}`}
                          </Text>
                        )}
                        {item.quantity && (
                          <Text textStyle="xs" color="fg.muted">
                            Quantity: {item.quantity} kg
                          </Text>
                        )}
                      </VStack>
                    )
                  }}
                />
              </Field>
            </VStack>
          )}

          <Heading size="md" as="h3">
            Provide the {formatActivityKind(createFormData.kind).toLowerCase()} dates
          </Heading>
          <HStack gap={2} width="100%" justifyContent="space-between">
            <Field label="Start" required>
              <Input
                name="start"
                type="datetime-local"
                value={createFormData.start || ""}
                onChange={(e) => {
                  lastCheckedParamsRef.current = null // Reset to allow re-check
                  updateCreateFormField("start", e.target.value)
                  setLastChangedField("start")
                }}
              />
            </Field>
            <Field label="End" required>
              <Input
                name="end"
                type="datetime-local"
                value={createFormData.end || ""}
                onChange={(e) => {
                  lastCheckedParamsRef.current = null // Reset to allow re-check
                  updateCreateFormField("end", e.target.value)
                  setLastChangedField("end")
                }}
              />
            </Field>
          </HStack>
        </VStack>
      </FormContainer>
    </ActivityCreateScreenWrapper>
  )
}

export default ActivityCreateHome
